/**
 * CatMilestoneModal — generalised celebration modal for every cat-count
 * milestone (1st, 5th, 10th, 25th). Each tier has its own copy + theme
 * (see /lib/milestones.js), and its own localStorage flag so a single
 * user never sees the same tier twice but DOES see each new tier as
 * their collection grows.
 *
 * Usage from anywhere:
 *   import { triggerCatMilestone } from "../components/CatMilestoneModal";
 *   triggerCatMilestone({ count, name, photo, catId });
 *
 * The component itself is mounted once globally inside Layout.
 *
 * Backwards-compat: `triggerFirstCatMilestone(payload)` (old API) still
 * works — it forwards to count=1. Kept so older imports don't break.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { PawPrint, Sparkles, X, Heart, Trophy } from "lucide-react";
import { Button } from "./ui/button";
import { findMilestone } from "../lib/milestones";
import { useI18n } from "../i18n";

let externalTrigger = null;

export function triggerCatMilestone(payload) {
  if (typeof window === "undefined") return;
  const ms = findMilestone(payload?.count);
  if (!ms) return; // No milestone for this count — silent no-op.
  if (localStorage.getItem(ms.id)) return; // User already celebrated this tier.
  if (!externalTrigger) {
    setTimeout(() => externalTrigger && externalTrigger(payload, ms), 50);
    return;
  }
  externalTrigger(payload, ms);
}

// Legacy alias.
export function triggerFirstCatMilestone(payload) {
  triggerCatMilestone({ ...(payload || {}), count: 1 });
}

const THEME = {
  amber: {
    overline: "linear-gradient(135deg, #fde68a 0%, #fb923c 100%)",
    overlineText: "#7c2d12",
    name: "linear-gradient(135deg, #e11d48, #ea580c, #f59e0b)",
    badge: "linear-gradient(135deg, #ea580c, #f59e0b)",
    shadow: "0 30px 60px rgba(234, 88, 12, 0.20)",
    cta: "bg-orange-600 hover:bg-orange-700",
    confetti: ["#e11d48", "#ea580c", "#f59e0b", "#10b981", "#fde68a", "#fff"],
  },
  orange: {
    overline: "linear-gradient(135deg, #fdba74 0%, #ea580c 100%)",
    overlineText: "#7c2d12",
    name: "linear-gradient(135deg, #ea580c, #f59e0b)",
    badge: "linear-gradient(135deg, #ea580c, #c2410c)",
    shadow: "0 30px 60px rgba(194, 65, 12, 0.25)",
    cta: "bg-orange-600 hover:bg-orange-700",
    confetti: ["#ea580c", "#f59e0b", "#fde68a", "#10b981", "#fff"],
  },
  rose: {
    overline: "linear-gradient(135deg, #fda4af 0%, #e11d48 100%)",
    overlineText: "#9f1239",
    name: "linear-gradient(135deg, #be123c, #e11d48, #f97316)",
    badge: "linear-gradient(135deg, #e11d48, #be123c)",
    shadow: "0 30px 60px rgba(225, 29, 72, 0.30)",
    cta: "bg-rose-600 hover:bg-rose-700",
    confetti: ["#be123c", "#e11d48", "#f43f5e", "#fb7185", "#fde68a", "#fff"],
  },
  purple: {
    overline: "linear-gradient(135deg, #d8b4fe 0%, #7c3aed 100%)",
    overlineText: "#5b21b6",
    name: "linear-gradient(135deg, #6d28d9, #c026d3, #e11d48)",
    badge: "linear-gradient(135deg, #7c3aed, #5b21b6)",
    shadow: "0 30px 60px rgba(124, 58, 237, 0.30)",
    cta: "bg-violet-600 hover:bg-violet-700",
    confetti: ["#6d28d9", "#a855f7", "#c026d3", "#e11d48", "#fde68a", "#fff"],
  },
};

export default function CatMilestoneModal() {
  const { lang } = useI18n();
  const isTr = lang === "tr";
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ name: "", photo: "", catId: null, ms: null });
  const firedRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    externalTrigger = (p, ms) => {
      if (firedRef.current) return;
      firedRef.current = true;
      setData({
        name: p?.name || (isTr ? "Yeni dostun" : "Your new friend"),
        photo: p?.photo || "",
        catId: p?.catId || null,
        ms,
      });
      setOpen(true);
      try { localStorage.setItem(ms.id, new Date().toISOString()); } catch { /* noop */ }
    };
    return () => { externalTrigger = null; };
  }, [isTr]);

  // Celebration burst — scaled by tier so bigger milestones feel bigger.
  useEffect(() => {
    if (!open || !data.ms) return;
    let cancelled = false;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const tierMultiplier = data.ms.count >= 25 ? 1.7 : data.ms.count >= 10 ? 1.35 : data.ms.count >= 5 ? 1.15 : 1;
    const baseCount = Math.round(70 * tierMultiplier);
    const colors = THEME[data.ms.theme].confetti;
    const fire = (origin) => {
      confetti({
        particleCount: baseCount,
        spread: 80,
        startVelocity: 42,
        gravity: 0.95,
        ticks: 220,
        scalar: 1.0,
        origin,
        colors,
        disableForReducedMotion: true,
      });
    };
    fire({ x: 0.25, y: 0.65 });
    const t1 = setTimeout(() => !cancelled && fire({ x: 0.75, y: 0.65 }), 180);
    const t2 = setTimeout(() => !cancelled && fire({ x: 0.5, y: 0.85 }), 380);
    // Heavy tiers get a 4th burst.
    let t3;
    if (data.ms.count >= 10) {
      t3 = setTimeout(
        () => !cancelled && fire({ x: 0.5, y: 0.5 }),
        620
      );
    }
    try {
      if (navigator.vibrate) {
        const pattern = data.ms.count >= 25 ? [40, 80, 40, 80, 60] : data.ms.count >= 10 ? [30, 80, 30, 80] : [20, 60, 20];
        navigator.vibrate(pattern);
      }
    } catch { /* noop */ }
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); if (t3) clearTimeout(t3); };
  }, [open, data.ms]);

  if (!open || !data.ms) return null;

  const ms = data.ms;
  const theme = THEME[ms.theme] || THEME.amber;
  const title = isTr ? ms.titleTr(data.name) : ms.titleEn(data.name);
  const subtitle = isTr ? ms.subtitleTr(data.name) : ms.subtitleEn(data.name);
  const overlineLabel = isTr ? ms.overline : ms.overlineEn;
  const reward = isTr ? ms.rewardTr : ms.rewardEn;

  const close = () => setOpen(false);
  const goToCat = () => { setOpen(false); if (data.catId) nav(`/cats/${data.catId}`); };

  return (
    <div
      className="first-cat-milestone-overlay"
      role="dialog"
      aria-modal="true"
      data-testid="cat-milestone-modal"
      data-milestone-count={ms.count}
    >
      <div
        className="first-cat-milestone-card"
        style={{ boxShadow: theme.shadow }}
      >
        <button
          type="button"
          onClick={close}
          className="first-cat-milestone-close"
          aria-label={isTr ? "Kapat" : "Close"}
          data-testid="cat-milestone-close"
        >
          <X size={18} />
        </button>

        <div
          className="first-cat-milestone-overline"
          style={{ background: theme.overline, color: theme.overlineText }}
        >
          {ms.count >= 10 ? <Trophy size={12} /> : <Sparkles size={12} />} {overlineLabel}
        </div>

        <div className="first-cat-milestone-photo-wrap">
          {data.photo ? (
            <img
              src={data.photo.startsWith("data:") ? data.photo : `data:image/jpeg;base64,${data.photo}`}
              alt={data.name}
              className="first-cat-milestone-photo"
            />
          ) : (
            <div className="first-cat-milestone-photo first-cat-milestone-photo--empty">
              <PawPrint size={36} className="text-orange-600" />
            </div>
          )}
          <div
            className="first-cat-milestone-badge"
            style={{ background: theme.badge }}
          >
            {ms.count >= 10 ? <Trophy size={14} /> : <PawPrint size={14} />}
          </div>
        </div>

        <h2 className="first-cat-milestone-title" data-testid="cat-milestone-title">
          {title.prefix}
          <span
            className="first-cat-milestone-name"
            style={{ background: theme.name, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}
          >
            {title.name}
          </span>
          {title.suffix}
        </h2>
        <p className="first-cat-milestone-subtitle">{subtitle}</p>

        {reward && (
          <div className="first-cat-milestone-reward" data-testid="cat-milestone-reward">
            <Trophy size={14} /> {reward}
          </div>
        )}

        <div className="first-cat-milestone-actions">
          <Button
            size="lg"
            onClick={goToCat}
            className={`rounded-2xl text-white font-bold gap-2 px-6 shadow-lg ${theme.cta}`}
            data-testid="cat-milestone-cta"
            disabled={!data.catId}
          >
            <Heart size={16} /> {isTr ? "Profili gör" : "View profile"}
          </Button>
          <button
            type="button"
            onClick={close}
            className="text-stone-500 hover:text-stone-900 font-semibold text-sm"
            data-testid="cat-milestone-dismiss"
          >
            {isTr ? "Daha sonra" : "Later"}
          </button>
        </div>
      </div>
    </div>
  );
}
